/**
 * AdminEngagement — Engagement & Conversion section of the Usage Stats dashboard.
 * All figures are human-only (bot-filtered) and session-based. Engagement,
 * scroll, visitor and channel metrics are collected from tracker v3 onward.
 */
import React, { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, AlertTriangle, Clock, HeartPulse, MousePointerClick, Percent, RefreshCw, Users } from 'lucide-react';
import api from '../lib/api';

function fmtS(s) {
  if (s == null) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m ${String(Math.round(s % 60)).padStart(2, '0')}s`;
}

function Delta({ v }) {
  if (v === undefined || v === null) return null;
  const pos = v >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${pos ? 'text-forest' : 'text-heritage'}`}>
      {pos ? <ArrowUp size={11} /> : <ArrowDown size={11} />}{Math.abs(v)}%
    </span>
  );
}

function Tile({ label, value, sub, delta, icon: Icon }) {
  return (
    <div className="bg-white border border-charcoal/10 rounded-2xl p-5" data-testid={`eng-tile-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        {Icon ? <Icon size={18} className="text-heritage" /> : <span />}
        <Delta v={delta} />
      </div>
      <div className="font-display text-3xl mb-1">{value ?? '—'}</div>
      <div className="text-xs text-muted">{label}{sub ? <span className="text-charcoal/40"> · {sub}</span> : null}</div>
    </div>
  );
}

function Bars({ rows, testId }) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <ul className="space-y-2.5" data-testid={testId}>
      {rows.map((r) => (
        <li key={r.label} className="text-sm">
          <div className="flex items-baseline justify-between gap-3 mb-1">
            <span className="font-medium truncate min-w-0 capitalize">{r.label}</span>
            <span className="text-charcoal/60 shrink-0 tabular-nums">{r.count.toLocaleString()}{r.pct != null ? ` · ${r.pct}%` : ''}</span>
          </div>
          <div className="h-1 bg-stone rounded-full overflow-hidden">
            <div className="h-full bg-heritage" style={{ width: `${(r.count / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function FunnelBar({ label, count, pctOfTop, stepPct, color }) {
  return (
    <div>
      <div className="flex items-baseline justify-between text-sm mb-1.5">
        <span className="font-semibold">{label}</span>
        <span className="tabular-nums">
          <strong>{count.toLocaleString()}</strong>
          {stepPct != null && <span className="text-muted ml-2">· {stepPct}% of previous step</span>}
        </span>
      </div>
      <div className="h-9 bg-stone rounded-lg overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500 flex items-center justify-end pr-3 text-xs text-base font-semibold`}
          style={{ width: `${Math.max(pctOfTop, 4)}%` }}>
          {pctOfTop >= 10 && `${Math.round(pctOfTop)}%`}
        </div>
      </div>
    </div>
  );
}

function ConvTable({ rows, label, testId }) {
  if (!rows?.length) return null;
  return (
    <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6" data-testid={testId}>
      <h3 className="font-display text-lg mb-4">Conversion by {label}</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-charcoal/50 border-b border-charcoal/10">
            <th className="py-2 pr-3">{label}</th>
            <th className="py-2 pr-3 text-right">Sessions</th>
            <th className="py-2 pr-3 text-right">Product page</th>
            <th className="py-2 text-right">Outbound</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="border-b border-charcoal/5 last:border-0">
              <td className="py-2 pr-3 font-medium capitalize">{r.label}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{r.sessions.toLocaleString()}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{r.detail_pct != null ? `${r.detail_pct}%` : '—'}</td>
              <td className="py-2 text-right tabular-nums">{r.outbound.toLocaleString()} · {r.outbound_pct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HealthStat({ label, pct, good }) {
  const ok = pct >= good;
  return (
    <div className="bg-stone/60 rounded-xl p-3 text-center" data-testid={`health-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <div className={`font-display text-2xl ${ok ? 'text-forest' : 'text-heritage'}`}>{pct}%</div>
      <div className="text-[11px] text-muted leading-tight mt-0.5">{label}</div>
    </div>
  );
}

export default function AdminEngagement({ qs }) {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    api.get(`/admin/analytics/engagement?${qs}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setErr(true); });
    api.get(`/admin/analytics/health?${qs}`)
      .then((r) => { if (alive) setHealth(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [qs]);

  if (err) return <div className="text-heritage text-sm">Couldn't load engagement metrics.</div>;
  if (!data) return <div className="text-muted text-sm py-8 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading engagement metrics…</div>;

  const c = data.current;
  const w = data.window;
  const f = c.funnel;
  const cmp = `vs ${w.prev_start} → ${w.prev_end}`;
  const depthRows = ['1', '2', '3-5', '6+'].map((k) => ({
    label: `${k} page${k === '1' ? '' : 's'}`,
    count: c.depth[k] || 0,
    pct: c.sessions ? Math.round(((c.depth[k] || 0) / c.sessions) * 1000) / 10 : 0,
  }));
  const channelRows = Object.entries(c.channels).map(([k, v]) => ({ label: k, count: v, pct: c.sessions ? Math.round((v / c.sessions) * 1000) / 10 : 0 }));
  const deviceRows = Object.entries(c.devices).map(([k, v]) => ({ label: k, count: v, pct: c.sessions ? Math.round((v / c.sessions) * 1000) / 10 : 0 }));
  const scroll = data.scroll;

  return (
    <div className="space-y-6" data-testid="engagement-section">
      <div className="flex items-baseline justify-between flex-wrap gap-2 border-t border-charcoal/10 pt-8">
        <h2 className="font-display text-3xl">Engagement &amp; Conversion</h2>
        <div className="text-xs text-muted">
          {w.start} → {w.end} · all changes {cmp}
          {data.v3_since && <span className="text-charcoal/40"> · engagement tracked since {data.v3_since}</span>}
        </div>
      </div>

      {/* Visitor + engagement tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Tile label="Unique visitors" value={c.unique_visitors.toLocaleString()} delta={data.deltas.unique_visitors} icon={Users} />
        <Tile label="Sessions" value={c.sessions.toLocaleString()} delta={data.deltas.sessions} icon={Users} />
        <Tile label="New vs returning" value={`${c.new_visitors.toLocaleString()} / ${c.returning_visitors.toLocaleString()}`} sub="visitors" icon={Users} />
        <Tile label="Pages per session" value={c.pages_per_session} delta={data.deltas.pages_per_session} icon={MousePointerClick} />
        <Tile label="Avg engaged time / session" value={fmtS(c.avg_engaged_s_per_session)} delta={data.deltas.avg_engaged_s_per_session} icon={Clock} />
        <Tile label="Avg engaged time / page" value={fmtS(c.avg_engaged_s_per_page)} icon={Clock} />
        <Tile label="Engagement rate" value={`${c.engagement_rate}%`} sub={`bounce ${c.bounce_rate}%`} delta={data.deltas.engagement_rate} icon={Percent} />
        <Tile label="Confirmed human sessions" value={`${c.confirmed_human_rate}%`} sub={`${c.confirmed_human_sessions.toLocaleString()} interacted`} icon={Users} />
      </div>

      {/* Session funnel */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6" data-testid="session-funnel">
        <h3 className="font-display text-xl mb-1">Conversion funnel — sessions</h3>
        <p className="text-xs text-muted mb-5">Landing → content page → attraction/business page → outbound partner click. Percentages of all human sessions in the period.</p>
        <div className="space-y-3">
          <FunnelBar label="Human sessions" count={f.sessions} pctOfTop={100} color="bg-heritage" />
          <FunnelBar label="Viewed a content / category page" count={f.content_sessions} pctOfTop={f.content_pct} stepPct={f.content_pct} color="bg-forest" />
          <FunnelBar label="Viewed an attraction / stay / event page" count={f.detail_sessions} pctOfTop={f.detail_pct} stepPct={f.content_to_detail_pct} color="bg-mustard" />
          <FunnelBar label="Clicked out to a tourism business" count={f.outbound_sessions} pctOfTop={f.outbound_pct} stepPct={f.detail_to_outbound_pct} color="bg-charcoal" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 text-center">
          {[
            { l: 'Explore → product page', v: f.explore_to_detail_pct, n: f.explore_sessions, note: 'of explore sessions' },
            { l: 'Product page → business', v: f.detail_to_outbound_pct, n: f.detail_sessions, note: 'of product sessions' },
            { l: 'Session → business referral', v: f.outbound_pct, n: f.sessions, note: 'of all sessions' },
            { l: 'Explore sessions', v: null, n: f.explore_sessions, note: 'visited /explore' },
          ].map((x) => (
            <div key={x.l} className="bg-stone/60 rounded-xl p-3" data-testid={`conv-${x.l.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <div className="font-display text-xl">{x.v != null ? `${x.v}%` : x.n.toLocaleString()}</div>
              <div className="text-[11px] text-muted">{x.l}{x.v != null ? ` · ${x.n.toLocaleString()} ${x.note}` : ''}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 mt-3 text-center">
          {[
            { l: 'Newsletter signup', v: c.ctas.newsletter_pct, n: c.ctas.newsletter_sessions },
            { l: 'Award vote', v: c.ctas.award_vote_pct, n: c.ctas.award_vote_sessions },
            { l: 'Itinerary built', v: c.ctas.itinerary_pct, n: c.ctas.itinerary_sessions },
          ].map((x) => (
            <div key={x.l} className="bg-stone/60 rounded-xl p-3">
              <div className="font-display text-xl">{x.v}%</div>
              <div className="text-[11px] text-muted">{x.l} · {x.n.toLocaleString()} sessions</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6">
          <h3 className="font-display text-lg mb-4">Sessions by pages viewed</h3>
          <Bars rows={depthRows} testId="depth-buckets" />
        </div>
        <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6">
          <h3 className="font-display text-lg mb-1">Scroll depth</h3>
          <p className="text-xs text-muted mb-4">Share of measured page visits reaching each milestone ({scroll.pages_measured.toLocaleString()} page visits measured).</p>
          <Bars testId="scroll-depth" rows={[
            { label: 'Reached 25%', count: scroll.pages_measured ? Math.round(scroll.p25 * scroll.pages_measured / 100) : 0, pct: scroll.p25 },
            { label: 'Reached 50%', count: scroll.pages_measured ? Math.round(scroll.p50 * scroll.pages_measured / 100) : 0, pct: scroll.p50 },
            { label: 'Reached 75%', count: scroll.pages_measured ? Math.round(scroll.p75 * scroll.pages_measured / 100) : 0, pct: scroll.p75 },
            { label: 'Reached 90%', count: scroll.pages_measured ? Math.round(scroll.p90 * scroll.pages_measured / 100) : 0, pct: scroll.p90 },
          ]} />
        </div>
        <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6">
          <h3 className="font-display text-lg mb-4">Traffic channels — sessions</h3>
          <Bars rows={channelRows} testId="channel-split" />
        </div>
        <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6">
          <h3 className="font-display text-lg mb-4">Device split — sessions</h3>
          <Bars rows={deviceRows} testId="device-split" />
        </div>
        <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6">
          <h3 className="font-display text-lg mb-1">Outbound clicks by action</h3>
          <p className="text-xs text-muted mb-4">What visitors were trying to do when they clicked out to a tourism business (tracker v4 onward).</p>
          {Object.keys(c.outbound_actions || {}).length === 0
            ? <div className="text-muted text-sm py-4 text-center">No classified outbound clicks yet.</div>
            : <Bars testId="outbound-actions" rows={Object.entries(c.outbound_actions).map(([k, v]) => ({ label: k, count: v }))} />}
        </div>
        <ConvTable rows={c.conversion_by_channel} label="traffic source" testId="conv-by-channel" />
        <ConvTable rows={c.conversion_by_device} label="device" testId="conv-by-device" />
        <ConvTable rows={c.conversion_by_content} label="content type" testId="conv-by-content" />
      </div>

      {/* Tracking health */}
      {health && (
        <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6" data-testid="tracking-health">
          <div className="flex items-center gap-2 mb-1">
            <HeartPulse size={18} className="text-heritage" />
            <h3 className="font-display text-xl">Tracking health</h3>
          </div>
          <p className="text-xs text-muted mb-4">
            Data-quality diagnostics for the window — {health.events_total.toLocaleString()} events
            ({health.human_events.toLocaleString()} human · {health.bot_events.toLocaleString()} bot).
            {health.baseline_v4_since && <span className="text-charcoal/40"> Tracker v4 baseline since {health.baseline_v4_since}.</span>}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2.5">
            <HealthStat label="Human traffic" pct={health.human_pct} good={25} />
            <HealthStat label="Bots filtered" pct={health.bot_pct} good={0} />
            <HealthStat label="Known source" pct={health.known_source_pct} good={70} />
            <HealthStat label="Known device" pct={health.known_device_pct} good={80} />
            <HealthStat label="Referrer / UTM" pct={health.with_referrer_pct} good={10} />
            <HealthStat label="Engagement events" pct={health.engage_coverage_pct} good={40} />
            <HealthStat label="Visitor ids" pct={health.visitor_id_pct} good={70} />
          </div>
          {health.anomalies?.length > 0 && (
            <ul className="mt-4 space-y-1.5" data-testid="health-anomalies">
              {health.anomalies.map((a) => (
                <li key={a} className="text-sm text-heritage flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {a}
                </li>
              ))}
            </ul>
          )}
          {health.top_bots?.length > 0 && (
            <div className="mt-4 text-xs text-muted" data-testid="health-top-bots">
              Top filtered bots: {health.top_bots.map((b) => `${b.name} (${b.events.toLocaleString()})`).join(' · ')}
            </div>
          )}
        </div>
      )}

      {/* Landing pages */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6" data-testid="landing-pages-table">
        <h3 className="font-display text-xl mb-1">Landing pages — ranked by human sessions</h3>
        <p className="text-xs text-muted mb-4">One-page rate + engaged time + scroll show whether single-page visitors actually consumed the content or abandoned immediately.</p>
        {c.landing_pages.length === 0 ? <div className="text-muted text-sm py-6 text-center">No data yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-charcoal/50 border-b border-charcoal/10">
                  <th className="py-2 pr-4">Landing page</th>
                  <th className="py-2 pr-4 text-right">Sessions</th>
                  <th className="py-2 pr-4 text-right">1-page rate</th>
                  <th className="py-2 pr-4 text-right">Avg engaged</th>
                  <th className="py-2 text-right">Avg scroll</th>
                </tr>
              </thead>
              <tbody>
                {c.landing_pages.map((r) => (
                  <tr key={r.path} className="border-b border-charcoal/5 last:border-0">
                    <td className="py-2.5 pr-4 font-medium truncate max-w-[280px]" title={r.path}>{r.path}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{r.sessions.toLocaleString()}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{r.one_page_rate}%</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{fmtS(r.avg_engaged_s)}</td>
                    <td className="py-2.5 text-right tabular-nums">{r.avg_scroll}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Exit pages */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6" data-testid="exit-pages">
        <h3 className="font-display text-lg mb-4">Exit pages — last page of the session</h3>
        {c.exit_pages.length === 0 ? <div className="text-muted text-sm py-4 text-center">No data yet.</div> : (
          <Bars rows={c.exit_pages.map((r) => ({ label: r.path, count: r.sessions }))} testId="exit-pages-bars" />
        )}
      </div>

      {/* Methodology */}
      <details className="bg-charcoal text-base rounded-2xl p-5 md:p-6" data-testid="methodology">
        <summary className="font-display text-lg cursor-pointer">What counts as a human page view, session and visitor?</summary>
        <dl className="mt-4 space-y-3 text-sm opacity-90">
          {Object.entries(data.definitions).map(([k, v]) => (
            <div key={k}>
              <dt className="font-semibold text-mustard capitalize">{k.replace(/_/g, ' ')}</dt>
              <dd className="mt-0.5 leading-relaxed">{v}</dd>
            </div>
          ))}
        </dl>
      </details>
    </div>
  );
}
